import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/daimderoh653ivfvogbg";

const leadTypes = ["新增事件线索", "补充证据素材", "纠错与更正", "合作与反馈"];

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function SubmitForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const guard = String(formData.get("contact_alt") ?? "").trim();
    if (guard) {
      setStatus("success");
      return;
    }

    const message = String(formData.get("message") ?? "");
    if (message.trim().length === 0) {
      setStatus("error");
      setFormError("请填写具体说明，方便我们核对来源。");
      return;
    }
    if (message.length > 500) {
      setStatus("error");
      setFormError("说明内容最多 500 字，请精简后再提交。");
      return;
    }

    formData.delete("contact_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, typeof value === "string" ? value : "");
      });

      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const success = response.ok && parsed?.code === "OK" && !isSpam;

      if (success) {
        setStatus("success");
        form.reset();
        setMessageLength(0);
        return;
      }

      setStatus("error");
      setFormError(typeof serverMsg === "string" && serverMsg ? serverMsg : "提交未成功，请稍后再试。");
    } catch {
      setStatus("error");
      setFormError("网络异常，未能提交，请检查网络后重试。");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-lg border border-secondary-300/50 bg-secondary-100/60 p-6 md:p-8 text-center">
        <span className="mx-auto w-12 h-12 flex items-center justify-center rounded-md bg-secondary-500 text-background-50 text-2xl">
          <i className="ri-check-line" />
        </span>
        <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">已收到，谢谢你的补充</h3>
        <p className="mt-2 text-sm leading-7 text-foreground-600">
          我们会先核对来源与时间，再决定是否收录进对应的证据合集。如果素材存在明显疑点，会在条目里标注为待核实。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-5 px-5 py-2.5 rounded-md border border-background-200 text-sm text-foreground-800 hover:border-primary-300 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
        >
          再提交一条
        </button>
      </div>
    );
  }

  return (
    <form
      id="lead-submit-form"
      data-readdy-form
      onSubmit={handleSubmit}
      noValidate
      className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-7"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="lead-name" className="block text-sm text-foreground-700 mb-2">
            怎么称呼你 <span className="text-accent-300">*</span>
          </label>
          <input
            id="lead-name"
            name="name"
            type="text"
            required
            placeholder="昵称或称呼即可"
            className="w-full h-11 px-4 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
          />
        </div>
        <div>
          <label htmlFor="lead-email" className="block text-sm text-foreground-700 mb-2">
            联系邮箱 <span className="text-accent-300">*</span>
          </label>
          <input
            id="lead-email"
            name="email"
            type="email"
            required
            placeholder="用于核对素材时联系你"
            className="w-full h-11 px-4 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <div>
          <label htmlFor="lead-type" className="block text-sm text-foreground-700 mb-2">
            线索类型 <span className="text-accent-300">*</span>
          </label>
          <select
            id="lead-type"
            name="lead_type"
            required
            defaultValue=""
            className="w-full h-11 px-4 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 cursor-pointer"
          >
            <option value="" disabled>
              请选择线索类型
            </option>
            {leadTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="lead-event" className="block text-sm text-foreground-700 mb-2">
            相关事件（选填）
          </label>
          <input
            id="lead-event"
            name="event_name"
            type="text"
            placeholder="例如：某个事件档案的名称"
            className="w-full h-11 px-4 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="lead-message" className="block text-sm text-foreground-700 mb-2">
          具体说明 <span className="text-accent-300">*</span>
        </label>
        <textarea
          id="lead-message"
          name="message"
          required
          rows={5}
          maxLength={500}
          onChange={(event) => setMessageLength(event.target.value.length)}
          placeholder="请尽量附上原始链接与大致时间，说明这条素材能证明什么。"
          className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-200 text-sm leading-7 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30 resize-none"
        />
        <div className="mt-1.5 text-right text-xs text-foreground-500">{messageLength} / 500</div>
      </div>

      <input
        className="field-guard"
        type="text"
        name="contact_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {status === "error" && formError && (
        <p role="alert" className="mt-4 flex items-start gap-2 text-sm text-accent-300">
          <i className="ri-error-warning-line mt-0.5" />
          {formError}
        </p>
      )}

      <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
          {status === "submitting" ? "提交中…" : "提交线索"}
        </button>
        <p className="text-xs leading-6 text-foreground-500">
          仅用于素材核对，不会公开你的联系方式。请勿提交他人隐私信息。
        </p>
      </div>
    </form>
  );
}